import { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import { ContentCopy, MoreVert, Tune } from "@mui/icons-material";
import DataGridButton from "../../components/DataGridButton";
import DropDownButton from "../../components/DropDownButton";
import { getStatusLabel } from "../../components/StatusChip";
import { tokens } from "../../theme";
import { envListButtonStyles } from "../../styles/buttonStyles";
import { HIBERNATE_WAKEUP_ENABLED_STATUS } from "../../utils/constants";
import { rowActionsStyles } from "./styles";

// Every row action lives in this trailing cell: the state action, refresh, the
// copy shortcut and the overflow menu. The handlers are owned by the page.
const RowActions = ({
  row,
  isRefreshing,
  handleHibernateWakeUp,
  handleRefresh,
  handleDelete,
  handleConvertToVPTC,
  handleAutomationRuns,
  handleAddLicences,
  onOpenEditable,
  onViewDetails,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);
  const runAndClose = (action) => () => {
    closeMenu();
    action();
  };

  if (!row?.memberRole) return null;

  const stateDisabled = !HIBERNATE_WAKEUP_ENABLED_STATUS.includes(row?.status);
  const refreshAllowedStatus =
    row?.status === "RUNNING" ||
    row?.status === "UPDATE_FAILED" ||
    row?.status === "WAKEUP_FAILED";
  const refreshDisabled = isRefreshing || !refreshAllowedStatus;
  const refreshDisabledReason = isRefreshing
    ? "Refresh already in progress"
    : !refreshAllowedStatus
    ? `Refresh isn't available while the environment is ${getStatusLabel(
        row?.status
      ).toLowerCase()}`
    : "";
  const isSplit =
    row?.status === "HIBERNATE_FAILED" || row?.status === "WAKEUP_FAILED";
  const isVptc =
    row?.meta?.service_plan === "VIRTUAL_PRIVATE_TESSELL_AT_CUSTOMER";

  return (
    <Box sx={rowActionsStyles.cell}>
      <Box sx={rowActionsStyles.stateSlot}>
        {isSplit ? (
          <DropDownButton
            disabled={stateDisabled}
            row={row}
            handleHibernateWakeUp={handleHibernateWakeUp}
          />
        ) : (
          <DataGridButton
            sx={{
              ...rowActionsStyles.button,
              ...envListButtonStyles(colors).rowActionState,
            }}
            onClick={() =>
              handleHibernateWakeUp(
                row,
                row?.status === "HIBERNATED" ? "WAKE UP" : "HIBERNATE"
              )
            }
            disabled={stateDisabled}
          >
            {row?.status === "HIBERNATED" ? "WAKE UP" : "HIBERNATE"}
          </DataGridButton>
        )}
      </Box>
      <Box sx={rowActionsStyles.refreshSlot}>
        <Tooltip title={refreshDisabledReason}>
          <span>
            <DataGridButton
              sx={{
                ...rowActionsStyles.button,
                ...envListButtonStyles(colors).rowActionNeutral,
              }}
              onClick={() => handleRefresh(row)}
              disabled={refreshDisabled}
            >
              REFRESH
            </DataGridButton>
          </span>
        </Tooltip>
      </Box>
      <Tooltip title="Members and schedules">
        <IconButton
          size="small"
          sx={rowActionsStyles.tuneIconButton(colors)}
          onClick={() => onOpenEditable?.(row)}
        >
          <Tune sx={rowActionsStyles.icon} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Copy repo config">
        <IconButton
          size="small"
          sx={rowActionsStyles.iconButton(colors)}
          onClick={() => navigator.clipboard.writeText(row?.repoConfig)}
        >
          <ContentCopy sx={rowActionsStyles.icon} />
        </IconButton>
      </Tooltip>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          sx={rowActionsStyles.iconButton(colors)}
          aria-haspopup="menu"
          aria-expanded={open ? "true" : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <MoreVert sx={rowActionsStyles.icon} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        elevation={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: rowActionsStyles.menuPaper(colors) } }}
      >
        <MenuItem onClick={runAndClose(() => onViewDetails?.(row))}>
          View details
        </MenuItem>
        <MenuItem onClick={runAndClose(() => handleAutomationRuns(row))}>
          Automation Runs
        </MenuItem>
        <MenuItem onClick={runAndClose(() => handleDelete(row))}>
          Delete
        </MenuItem>
        {row?.meta?.service_plan === "BUSINESS" && row?.status === "RUNNING" ? (
          <MenuItem onClick={runAndClose(() => handleConvertToVPTC(row))}>
            Convert to VPT@C
          </MenuItem>
        ) : null}
        {isVptc && row?.status === "RUNNING" ? (
          <MenuItem onClick={runAndClose(() => handleAddLicences(row))}>
            Add Licences
          </MenuItem>
        ) : null}
      </Menu>
    </Box>
  );
};

export default RowActions;
